/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unicorn/no-for-each */
import { toast, ToastContent } from "react-toastify";

import {
  isUnrenderableErrorText,
  readValidatorEntry,
  reportUnrenderableErrorText,
  toRenderableErrorText,
} from "utils/get-error-message";

export const handleError = (err: any, fallback?: string) => {
  console.error(err);

  const status = err?.response?.status;

  // Gateway / proxy error pages (HTML) must never reach a toast: they leak
  // markup and grow tall enough to cover the dialog behind them.
  if (isUnrenderableErrorText(err?.response?.data)) {
    toast.error(reportUnrenderableErrorText(err.response.data, status));
    return;
  }

  if (isUnrenderableErrorText(err)) {
    // Report the body either way, but a caller that supplied a fallback knows
    // its own context better than the generic sentence does.
    const generic = reportUnrenderableErrorText(err);
    toast.error(fallback || generic);
    return;
  }

  // A short plain-text body ("Unauthorized"). None of the object branches below
  // match a string, so without this it fell through to toasting the axios error
  // object itself. `getErrorMessage` already handles this input; the two
  // helpers should not disagree.
  if (typeof err?.response?.data === "string" && err.response.data) {
    toast.error(toRenderableErrorText(err.response.data, status));
    return;
  }

  if (err && err.response && err.response.data) {
    // `errors` is sometimes a bare string (sked-api's validator catch sends
    // `{ errors: message }`). A string has `.length`, so without this it took
    // the array branch and threw `errors.forEach is not a function` out of the
    // error handler itself: no toast at all, and the throw escaping into
    // whatever catch called this.
    if (typeof err.response.data.errors === "string") {
      toast.error(toRenderableErrorText(err.response.data.errors, status));
    } else if (
      Array.isArray(err.response.data.errors) &&
      err.response.data.errors.length > 0
    ) {
      err.response.data.errors.forEach((error: ToastContent) => {
        // The ARRAY form is express-validator's `errors.array()`, whose entries
        // are objects carrying `msg`. Passing one straight to `toast.error` as
        // a `ToastContent` makes React throw "Objects are not valid as a React
        // child" and the user sees nothing at all, so read it the same way the
        // map form is read. A genuine React node still passes through.
        const text = readValidatorEntry(error);
        if (text) {
          toast.error(toRenderableErrorText(text, status));
          return;
        }
        toast.error(
          typeof error === "string"
            ? toRenderableErrorText(error, status)
            : error
        );
      });
    } else if (
      err.response.data.errors &&
      typeof err.response.data.errors === "object" &&
      Object.keys(err.response.data.errors).length > 0
    ) {
      // Only when every value is an ENTRY OBJECT carrying text. That rejects
      // `{ status: "failed", code: "RATE_LIMIT" }`, whose bare string values
      // would otherwise become two toasts reading "failed" and "RATE_LIMIT",
      // the exact prose `extractApiError` is gated against. It deliberately
      // accepts a plain `{ email: { message: "..." } }`: under an explicit
      // `errors` key that is a real reason, and the strict express-validator
      // shape is only needed where a WHOLE body is in question.
      const entries = Object.values(err.response.data.errors);
      const isEntryMap = entries.every(
        (entry) =>
          !!entry &&
          typeof entry === "object" &&
          !Array.isArray(entry) &&
          readValidatorEntry(entry) !== null
      );
      if (isEntryMap) {
        entries.forEach((error: any) => {
          // express-validator entries carry `msg`, not `message`.
          const text = readValidatorEntry(error);
          if (text) toast.error(toRenderableErrorText(text, status));
        });
      } else if (fallback) {
        toast.error(fallback);
      } else {
        toast.error(reportUnrenderableErrorText(err.response.data, status));
      }
    } else if (err.response.data.message) {
      // return message from the body of the response if present instead of generic
      toast.error(
        typeof err.response.data.message === "string"
          ? toRenderableErrorText(err.response.data.message, status)
          : err.response.data.message
      );
    } else if (err.response.data.status === 404) {
      if (fallback) {
        toast.error(fallback);
      } else {
        toast.error("The requested resource was not found.");
      }
    } else if (err.response.data.error) {
      toast.error(
        typeof err.response.data.error === "string"
          ? toRenderableErrorText(err.response.data.error, status)
          : err.response.data.error
      );
    } else {
      toast.error(err || fallback);
    }
  } else {
    // in case it returns 404
    if (err && err.error) {
      toast.error(
        typeof err.error === "string"
          ? toRenderableErrorText(err.error)
          : err.error
      );
    } else if (fallback) {
      toast.error(fallback);
    } else {
      toast.error(toRenderableErrorText(`${err}`));
    }
  }
};
